import { useEffect, useState, useCallback, useMemo } from "react";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import { DynamicFilterConfig } from "../../components/DynamicComponent/DynamicFilterPanel";
import { Link, useNavigate, useLocation } from "react-router";
import { useDispatch } from "react-redux";
import endpoints from "../../services/endpoints";
import { setPreviousUrl } from "../../store/slices/geolocationSlice";
import GridTable from "../../components/GridTable/GridTable";


export default function ListOfLogs() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { logs, reverseFundLogs, fundTransferLogs } = endpoints;
  const [fetchListTrigger, setFetchListTrigger] = useState<boolean>(false);
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>('log id');
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);

  const currentPath = location.pathname;
  const isUserActivityLog = currentPath.includes('/user-activity-logs');
  const isFundTransferLog = currentPath.includes('/fund-transfer-logs');
  const isReverseFundLog = currentPath.includes('/reverse-fund-logs');

  const currentPlaceholders = useMemo(() => {
    if (isReverseFundLog || isFundTransferLog) return ['transaction id', 'user name', 'user code'];
    return ['log id', 'name'];
  }, [isReverseFundLog, isFundTransferLog]);
  const rotatePlaceholder = useCallback(() => {
    setIsTransitioning(true);
    setTimeout(() => {
      setSearchPlaceholder(prev => {
        const currentIndex = currentPlaceholders.indexOf(prev);
        const nextIndex = (currentIndex + 1) % currentPlaceholders.length;
        return currentPlaceholders[nextIndex];
      });
      setIsTransitioning(false);
    }, 500);
  }, [currentPlaceholders]);

  const userActivityTableDetails: { [key: string]: any } = {
    "Log ID": {
      id: "log_id",
    },
    "Date and Time": {
      id: "date",
      sort: true,
      custom: ["date_time"],
    },
    "Initiated By": {
      id: "initiated_by",
    },
    "Action Taken On": {
      id: "action_taken_on",
      custom: ["action_taken_on"],
    },
    "Action Taken": {
      id: "action_taken",
      custom: ["action_taken"],
      className: "min-w-[115px]",
    },
    "Initiated User Location": {
      id: "initiated_by_location",
      className: "min-w-[220px]",
    },
    "Action Taken User Location": {
      id: "action_taken_on_location",
      className: "min-w-[220px]",
    },
    Actions: {
      id: "actions",
      actionTypes: ["view"],
    },
  };

  const fundTransferTableDetails: { [key: string]: any } = {
    "Log ID": {
      id: "log_id",
    },
    "Date and Time": {
      id: "created_at",
      sort: true,
      custom: ["date_time"],
      className: "min-w-[200px]",
    },
    "Transaction ID": {
      id: "transaction_id",
    },
    "Sender": {
      id: "sender",
    },
    "Receiver": {
      id: "receiver",
    },
    "Amount": {
      id: "amount",
      custom: ["amount"],
    },
    "Transaction Type": {
      id: "transaction_type",
      className: "min-w-[200px]",
    },
    "Wallet Type": {
      id: "wallet_type",
      custom: ['wallet_type']
    },
    "Channel": {
      id: "source_channel",
    },
    "Sender User Location": {
      id: "sender_location",
      className: "min-w-[250px]",
    },
    "Receiver User Location": {
      id: "receiver_location",
      className: "min-w-[250px]",
    },
    Actions: {
      id: "actions",
      actionTypes: ["view", "viewLogs"],
      isThreeDots: true,
    },
  };

  const reverseFundTableDetails: { [key: string]: any } = {
    "Log ID": {
      id: "log_id",
    },
    "Date and Time": {
      id: "created_at",
      sort: true,
      custom: ["created_at"],
      className: "min-w-[200px]",
    },
    "Action Taken By": {
      id: "action_taken_by",
    },
    "Action Taken": {
      id: "action_taken",
      className: "min-w-[115px]",
    },
    "Transaction ID": {
      id: "transaction_id",
    },
    "Type of Transaction": {
      id: "transaction_type",
    },
    "Sender": {
      id: "sender",
    },
    "Receiver": {
      id: "receiver",
    },
    "Sender User Location": {
      id: "sender_location",
      className: "min-w-[250px]",
    },
    "Receiver User Location": {
      id: "receiver_location",
      className: "min-w-[250px]",
    },
    "Reversal Raised Date": {
      id: "flagged_at",
      custom: ["flagged_at"],
      className: "min-w-[200px]",
    },
    "Amount": {
      id: "amount",
      custom: ["amount"],
      className: "min-w-[120px]",
    },
    Actions: {
      id: "actions",
      actionTypes: ["view"],
    },
  };

  const handleActions = async (id: string, key: string, text?: any) => {
    dispatch(setPreviousUrl(location.pathname + location.search));
    if (isReverseFundLog) {
      navigate(`/logs/user-activity-logs/${id}?log_type=Reversal`);
    } else if (isFundTransferLog) {
      if (key === 'viewLogs') {
        // Pass the row data to show json_logs in modal
        // The text parameter contains the full row data
        return;
      } else {
        navigate(`/logs/fund-transfer-logs/${id}`);
      }
    } else {
      navigate(`/logs/user-activity-logs/${id}`);
    }
  };
  const staticText = {
    emptyScreenText: {
      heading1: isReverseFundLog ? "There are no reverse fund logs yet" : isFundTransferLog ? "There are no fund transfer logs yet" : "There are no user activity logs yet",
      heading2: "Please check back later",
    },
  };
  const userActivityFilterConfig: DynamicFilterConfig = {
    "Date Range": {
      label: "Date Range",
      type: "date",
      required: false,
      searchKey: {
        start: "start_date",
        end: "end_date",
      },
    },
    "User Role": {
      label: "User Role",
      type: "checkbox",
      options: ["Master Distributor", "Distributor", "Sub Distributor", "Agent", "Retailer", "Customer", "Temporary User"],
      required: false,
      searchKey: "user_role",
      valueMapping: {
        "Master Distributor": "MDST",
        "Distributor": "DST",
        "Sub Distributor": "SDST",
        "Agent": "AGT",
        "Retailer": "RET",
        "Customer": "CMR",
        "Temporary User": "TMP",
      },
      reverseMapping: {
        "MDST": "Master Distributor",
        "DST": "Distributor",
        "SDST": "Sub Distributor",
        "AGT": "Agent",
        "RET": "Retailer",
        "CMR": "Customer",
        "TMP": "Temporary User",
      },
    },
    "Action Taken": {
      label: "Action Taken",
      type: "checkbox",
      options: ["Add", "Edit", "Activate", "Deactivate", "Reset PIN"],
      required: false,
      searchKey: "action_taken",
    },
  };

  const fundTransferFilterConfig: DynamicFilterConfig = {
    "Date Range": {
      label: "Date Range",
      type: "date",
      required: false,
      searchKey: {
        start: "start_date",
        end: "end_date",
      },
    },
    "Transaction Type": {
      label: "Transaction Type",
      type: "radio",
      options: ["All", "eTop Up Stock", "Recharge", "Fund Transfer", "Wallet Transfer"],
      default: "All",
      required: false,
      searchKey: "transaction_category",
    },
    "Wallet Type": {
      label: "Wallet Type",
      type: "radio",
      options: ["All", "eTop Up", "Digital Wallet"],
      default: "All",
      required: false,
      searchKey: "wallet_type",
    },
  };

  const reverseFundFilterConfig: DynamicFilterConfig = {
    "Date Range": {
      label: "Date Range",
      type: "date",
      required: false,
      searchKey: {
        start: "start_date",
        end: "end_date",
      },
    },  
    "Transaction Type": {
      label: "Transaction Type",
      type: "radio",
      options: ["All", "Recharge", "Wallet Transfer", "Fund Transfer", "eTop Up Request"],
      default: "All",
      required: false,
      searchKey: "transaction_category",
    },
    "Action Taken": {
      label: "Action Taken",
      type: "radio",
      options: ["All", "Approved", "Rejected"],
      default: "All",
      required: false,
      searchKey: "action_taken",
    },
  };

  // Add default sort to URL if not present
  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    if (!currentUrl.searchParams.has('sort')) {
      const defaultSort = (isReverseFundLog || isFundTransferLog) ? 'created_at+desc' : 'date+desc';
      currentUrl.searchParams.set('sort', defaultSort);
      navigate(currentUrl.pathname + currentUrl.search, { replace: true });
    }
  }, [isUserActivityLog, isFundTransferLog, isReverseFundLog, navigate]);

  // Trigger API call when tab changes
  useEffect(() => {
    setFetchListTrigger(prev => !prev);
    setSearchPlaceholder(currentPlaceholders[0]);
  }, [currentPath]);

  // Rotate placeholder animation
  useEffect(() => {
    const interval = setInterval(rotatePlaceholder, 2500);
    return () => clearInterval(interval);
  }, [rotatePlaceholder]);

  const renderExtraHeading = () => {
    // const currentSearchParams = new URLSearchParams(location.search);
    // const sortParam = currentSearchParams.get('sort');

    return (
      <div className="flex flex-row gap-4 sm:gap-6 pt-6 pb-3 px-4 sm:pl-4 overflow-x-auto whitespace-nowrap">
        <div className={`text-subTitle ${isUserActivityLog ? 'text-p_primary border-b-2 border-p_primary' : 'text-gray-400 border-b-2 border-gray-400'}`}>
          <Link
            to={`/logs/user-activity-logs?sort=date%2Bdesc`}
          >
            User Activity Logs
          </Link>
        </div>
        <div className={`text-subTitle ${isFundTransferLog ? 'text-p_primary border-b-2 border-p_primary' : 'text-gray-400 border-b-2 border-gray-400'}`}>
          <Link
            to={`/logs/fund-transfer-logs?sort=created_at%2Bdesc`}
          >
            Fund Transfer Logs
          </Link>
        </div>
        <div className={`text-subTitle ${isReverseFundLog ? 'text-p_primary border-b-2 border-p_primary' : 'text-gray-400 border-b-2 border-gray-400'}`}>
          <Link
            to={`/logs/reverse-fund-logs?sort=created_at%2Bdesc`}
          >
            Reverse Fund Logs
          </Link>
        </div>
      </div>
    );
  };

  return (
    <>
      <Breadcrumbs
        navPath={["Logs", isReverseFundLog ? "Reverse Fund Logs" : isFundTransferLog ? "Fund Transfer Logs" : "User Activity Logs", isReverseFundLog ? `List of Reverse Fund Logs` : isFundTransferLog ? `List of Fund Transfer Logs` : `List of User Activity Logs`]}
        heading={isReverseFundLog ? `List of Reverse Fund Logs` : isFundTransferLog ? `List of Fund Transfer Logs` : `List of User Activity Logs`}
        noNavigation={true}
        list={true}
      />
      <GridTable
        staticText={staticText}
        mainEndPoint={isReverseFundLog ? reverseFundLogs : isFundTransferLog ? fundTransferLogs : logs}
        TableDetails={
          isReverseFundLog ? reverseFundTableDetails : isFundTransferLog ? fundTransferTableDetails : userActivityTableDetails
        }
        handleActions={(id: string, key: string, text?: string) =>
          handleActions(id, key, text)
        }
        params={{ ...(isUserActivityLog ? { log_type: "User Activity" } : {}) }}
        reportType={isFundTransferLog ? "Fund Transfer Logs" : isUserActivityLog ? "User Activity Logs" : undefined}
        searchPlaceholder={`Search by ${searchPlaceholder}`}
        placeholderClassName={`search-placeholder ${isTransitioning ? 'transitioning' : ''}`}
        filters={true}
        exporting={false}
        notfoundRoute={{
          state: { route: isReverseFundLog ? `/logs/reverse-fund-logs` : isFundTransferLog ? `/logs/fund-transfer-logs` : `/logs/user-activity-logs` },
        }}
        title={isReverseFundLog ? `List of Reverse Fund Logs` : isFundTransferLog ? `List of Fund Transfer Logs` : `List of User Activity Logs`}
        hasViewMDPrivilege={true}
        addMorePadding={false}
        dynamicFilterConfig={isReverseFundLog ? reverseFundFilterConfig : isFundTransferLog ? fundTransferFilterConfig : userActivityFilterConfig}
        heightClassName="h-userActivityLogsHeight"
        renderExtraHeading={renderExtraHeading}
        fetchListTrigger={fetchListTrigger}
        filterShimmer={true}
        customHeight="h-[130px]"
        forceFullWidth={true}
        customEmptyScreenHeight={"h-[13rem] mt-4"}
        mainDivScroll={true}
      />
    </>
  );
}
